import React from 'react';

import classNames from 'classnames';
import moment from 'moment';

import { formatFigure } from 'Helpers/formatService';

import DiamondIcon from 'Assets/icons/diamond.svg';
import StarBadgeIcon from 'Assets/icons/star-badge.svg';

const BLANK_PLACEHOLDER = '—';

// Variance is a signed day count; the API sends BLANK_PLACEHOLDER when there is
// no estimated date, which renders blank here.
const formatVariance = (value) => formatFigure(value === BLANK_PLACEHOLDER ? null : value, String);

// Dates arrive from the API as ISO Y-m-d strings. Render them in the app's
// standard DD-MM-YYYY display format (see Helpers/dateUtils formatDate), parsing
// with moment so a date-only value is never shifted by the local timezone.
const formatDateCell = (value) => (value ? moment(value).format('DD-MM-YYYY') : BLANK_PLACEHOLDER);

// RAG dot colours mirror BudgetService::getRagStatus ('red' | 'amber' | 'green').
// Default Tailwind shades are used (and live in the config safelist) so amber
// has a colour — the project palette has no amber/yellow token.
const ragColourClass = (rag) =>
    classNames('tw-inline-block tw-w-2.5 tw-h-2.5 tw-rounded-full', {
        '!tw-bg-red-500': rag === 'red',
        '!tw-bg-yellow-400': rag === 'amber',
        '!tw-bg-green-500': rag === 'green',
        'tw-bg-charcoal-25': rag !== 'red' && rag !== 'amber' && rag !== 'green',
    });

const statusBadgeClass = (status) =>
    classNames('tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium', {
        // 0 = Not Started, 1 = In Progress, 2 = Complete
        'tw-bg-charcoal-15 tw-text-charcoal': status === 0 || status === '0' || status === null || status === undefined,
        'tw-bg-blue/15 tw-text-blue': status === 1 || status === '1',
        'tw-bg-green/15 tw-text-green': status === 2 || status === '2',
    });

/**
 * A single detailed activity row for the Project Plan report.
 *
 * Reads the row keys emitted by ProjectPlanReportService::deriveRow:
 *   { id, description, category, status, status_label, type, is_key_milestone,
 *     rag_status, is_overdue, owner, start_date, target_completion_date,
 *     estimated_completion_date, variance, is_exception }
 *
 * `columns` is the resolved set of visible value columns for this render.
 */
const ProjectPlanReportTableRow = ({ row, columns }) => {
    const isMilestone = row.type === 'milestone';

    return (
        <tr
            className={classNames('tw-text-xs !tw-bg-white', {
                '!tw-bg-red/5': row.is_overdue,
            })}
        >
            <td className="!tw-bg-charcoal-15 tw-text-charcoal">
                <div className="tw-flex tw-items-center tw-gap-x-1.5">
                    {row.is_key_milestone ? (
                        <StarBadgeIcon className="tw-h-3 tw-w-3 tw-text-blue tw-shrink-0" title={$t('Key milestone')} />
                    ) : (
                        isMilestone && <DiamondIcon className="tw-h-2.5 tw-w-2.5 tw-text-charcoal-50 tw-shrink-0" title={$t('Milestone')} />
                    )}
                    <span>{row.description}</span>
                </div>
            </td>

            {columns.status && (
                <td>
                    <span className={statusBadgeClass(row.status)}>{row.status_label || BLANK_PLACEHOLDER}</span>
                </td>
            )}

            {columns.rag_status && (
                <td>
                    <span className={ragColourClass(row.rag_status)} title={row.rag_status} />
                </td>
            )}

            {columns.owner && <td>{row.owner || BLANK_PLACEHOLDER}</td>}

            {columns.start_date && <td className="inbud-date-cell">{formatDateCell(row.start_date)}</td>}

            {columns.target_completion_date && <td className="inbud-date-cell">{formatDateCell(row.target_completion_date)}</td>}

            {columns.estimated_completion_date && <td className="inbud-date-cell">{formatDateCell(row.estimated_completion_date)}</td>}

            {columns.variance && <td className={classNames('inbud-numeric-cell', { '!tw-text-red': row.is_exception })}>{formatVariance(row.variance)}</td>}
        </tr>
    );
};

export default ProjectPlanReportTableRow;
